import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  Grid,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import Loader from "../../../components/Loader";
import { showSnackbar } from "../../../slice/snackbarSlice";
import {
  getCustomTasks,
  getDueDateHistory,
  getGenericInstructionTasks,
  getIndividualDueDateHistory,
  getIndividualTasks,
  updateDueDate,
  updateIndividualDueDate,
} from "../../../thunks/instruction";
import { NOTIFICATION_MESSAGE, VALIDATION_MSG } from "../../../utils/constant";
import {
  getClientTaskDueDateHistory,
  updateClientTaskDueDate,
} from "../../../thunks/client";
import { format } from "date-fns";
import moment from "moment";

const DueDateDialog = ({
  open,
  handleClose = () => {},
  selectedData = {},
  getThingsToDoData = () => {},
  isIndividual = false,
  isDashboardTask = false,
  getThingsToPickData = () => {},
  getV2Tasks = () => {},
  isClientTask = false,
  isV2Dashboard = false,
}) => {
  const dispatch = useDispatch();
  const params = useParams();
  const [isLoader, setLoader] = useState(false);
  const [dueDateHistory, setDueDateHistory] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [dateWarn, setDateWarn] = useState(false);
  const [updateDueDateData, setOpenDueDateData] = useState({
    dueDate: new Date(selectedData?.dueDate),
    note: "",
  });
  const [isError, setIsError] = useState(false);

  // pagination state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Add useEffect to fetch history when component opens
  useEffect(() => {
    if (open && tabValue === 0) {
      setPage(1);
      setDueDateHistory([]);
      if (isClientTask) {
        getIndividualDueDateHistoryData(1, true);
        return;
      }
      if (isIndividual) {
        getIndividualDueDateHistoryData(1, true);
      } else {
        getDueDateHistoryData(1, true);
      }
    }
  }, [open, selectedData?.id]); // Dependency on open and selectedData.id

  // Method to Handle change tab value
  const handleChangeTabValue = (event, newVal) => {
    setTabValue(newVal);
    if (newVal === 0) {
      setPage(1);
      setDueDateHistory([]);
      if (isClientTask) {
        getIndividualDueDateHistoryData(1, true);
        return;
      }
      if (isIndividual) {
        getIndividualDueDateHistoryData(1, true);
      } else {
        getDueDateHistoryData(1, true);
      }
    }
  };

  const handleUpdateValue = (value, key) => {
    setIsError(false);
    setOpenDueDateData({ ...updateDueDateData, [key]: value });
    if (key === "dueDate") {
      const selectedDate = new Date(value);
      const now = new Date();
      if (selectedDate < now) {
        setDateWarn(true);
      } else {
        setDateWarn(false);
      }
    }
  };

  /* ------------------- API CALLS ------------------- */

  const getDueDateHistoryData = async (currentPage = 1, reset = false) => {
    try {
      if (currentPage === 1) {
        setLoader(true);
      } else {
        setIsLoadingMore(true);
      }
      const payload = {
        page: currentPage,
        limit: 10,
        taskIds: [selectedData?.id],
      };
      const response = await dispatch(getDueDateHistory(payload)).unwrap();

      if (reset) {
        setDueDateHistory(response?.data || []);
      } else {
        setDueDateHistory((prev) => [...prev, ...(response?.data || [])]);
      }

      setHasMore(response?.data?.length === 10);
      setLoader(false);
      setIsLoadingMore(false);
    } catch (error) {
      setDueDateHistory([]);
      setLoader(false);
      setIsLoadingMore(false);
    }
  };

  const getIndividualDueDateHistoryData = async (
    currentPage = 1,
    reset = false
  ) => {
    try {
      if (currentPage === 1) {
        setLoader(true);
      } else {
        setIsLoadingMore(true);
      }
      const response = !isClientTask
        ? await dispatch(
            getIndividualDueDateHistory({
              id: selectedData?.id,
              page: currentPage,
              limit: 10,
            })
          ).unwrap()
        : await dispatch(
            getClientTaskDueDateHistory({
              id: selectedData?.id,
              page: currentPage,
              limit: 10,
            })
          ).unwrap();

      if (reset) {
        setDueDateHistory(response || []);
      } else {
        setDueDateHistory((prev) => [...prev, ...(response?.data || [])]);
      }

      setHasMore(response?.data?.length === 10);
      setLoader(false);
      setIsLoadingMore(false);
    } catch (error) {
      setDueDateHistory([]);
      setLoader(false);
      setIsLoadingMore(false);
    }
  };

  /* ------------------- Infinite Scroll Handler ------------------- */
  const handleScroll = (e) => {
    if (isLoadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      const nextPage = page + 1;
      setPage(nextPage);
      if (isClientTask) {
        getIndividualDueDateHistoryData(nextPage);
      } else if (isIndividual) {
        getIndividualDueDateHistoryData(nextPage);
      } else {
        getDueDateHistoryData(nextPage);
      }
    }
  };

  /* ------------------- UI ------------------- */

  const formatDate = (date) =>
    date ? format(new Date(date), "dd/MM/yyyy hh:mm a") : "NA";

  const formatHistory = (item) => {
    if (item.sourceTable === "InstructionTaskDueDatePusherHistory") {
      return (
        <>
          Task <b>{item.taskName}</b> was due by{" "}
          <b>{formatDate(item.pastDueDate)}</b> pushed to{" "}
          <b>{formatDate(item.newDueDate)}</b> by <b>{item.userName || "NA"}</b>{" "}
          on <b>{formatDate(item.createdAt)}</b> for reason:{" "}
          <b>{item.notes || "NA"}</b>
        </>
      );
    } else if (item.sourceTable === "InstructionTaskHistory") {
      return (
        <>
          Task <b>{item.taskName}</b> status changed to{" "}
          <b>{item.status || "NA"}</b> by <b>{item.userName || "NA"}</b> on{" "}
          <b>{formatDate(item.createdAt)}</b>
        </>
      );
    }
  };

  /* Success method to update instruction task due date */
  const handleUpdateDueDate = async () => {
    if (!updateDueDateData?.note) {
      setIsError(true);
    } else {
      try {
        const payload = {
          id: selectedData?.id,
          dueDate: updateDueDateData?.dueDate,
          note: updateDueDateData?.note,
        };
        setLoader(true);
        const response = !isClientTask
          ? await dispatch(updateDueDate(payload)).unwrap()
          : await dispatch(updateClientTaskDueDate(payload)).unwrap();
        if (response) {
          setLoader(false);
          dispatch(
            showSnackbar({
              show: true,
              message: NOTIFICATION_MESSAGE?.DUE_DATE_UPDATE_SUCCESS,
              severity: "success",
            })
          );
          if (isDashboardTask) {
            getThingsToDoData();
            getThingsToPickData();
          } else if (isV2Dashboard) {
            await getV2Tasks();
          } else {
            dispatch(getGenericInstructionTasks(params?.id));
          }
        }
        handleClose();
      } catch (error) {
        setLoader(false);
        dispatch(
          showSnackbar({
            show: true,
            message: error?.message,
            severity: "error",
          })
        );
        handleClose();
      }
    }
  };

  /* Success method to update individual/custom task due date */
  const handleUpdateIndividualDueDate = async () => {
    if (!updateDueDateData?.note) {
      setIsError(true);
    } else {
      try {
        const payload = {
          id: selectedData?.id,
          dueDate: updateDueDateData?.dueDate,
          note: updateDueDateData?.note,
        };

        setLoader(true);
        const response = await dispatch(
          updateIndividualDueDate(payload)
        ).unwrap();

        if (response) {
          setLoader(false);
          dispatch(
            showSnackbar({
              show: true,
              message: NOTIFICATION_MESSAGE?.DUE_DATE_UPDATE_SUCCESS,
              severity: "success",
            })
          );
          if (isDashboardTask) {
            getThingsToDoData();
            getThingsToPickData();
          } else if (selectedData?.taskType === "Instance" && !isV2Dashboard) {
            dispatch(getIndividualTasks(params?.id));
          } else if (
            isV2Dashboard ||
            (selectedData?.taskType === "Instance" && isV2Dashboard)
          ) {
            getV2Tasks();
          } else {
            dispatch(getCustomTasks(params?.id));
          }
        }
        handleClose();
      } catch (error) {
        setLoader(false);
        dispatch(
          showSnackbar({
            show: true,
            message: error?.message,
            severity: "error",
          })
        );
        handleClose();
      }
    }
  };

  // Method to update due date of custom /individual / instruction task
  const handleClickUpdateDueDate = () => {
    if (isClientTask) {
      handleUpdateDueDate();
      return;
    }
    if (isIndividual) {
      handleUpdateIndividualDueDate();
    } else {
      handleUpdateDueDate();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth={true}
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "28px",
          paddingBottom: 0,
          maxHeight: "500px",
          width: "90%",
          maxWidth: "800px",
        },
      }}
    >
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers={true} sx={{ marginRight: "5px" }}>
        {isLoader && <Loader />}
        <Grid item xs={12}>
          <Tabs
            fullWidth
            value={tabValue}
            onChange={handleChangeTabValue}
            aria-label="Due Date"
            sx={{
              "& .MuiTabs-indicator": {
                backgroundColor: "#00CE3F",
              },
              "& .MuiTab-root.Mui-selected": {
                color: "#00CE3F",
                fontWeight: 600,
              },
              "& .MuiTab-root": {
                color: "#3D4740",
              },
            }}
          >
            <Tab label="View History" />
            <Tab label="Update Due Date" />
          </Tabs>
        </Grid>

        {/* View History Component with infinite scroll */}
        {tabValue === 0 && (
          <Grid
            container
            spacing={3}
            mt={2}
            onScroll={handleScroll}
            sx={{ maxHeight: "400px", overflowY: "auto" }}
          >
            {dueDateHistory?.length > 0 &&
              dueDateHistory.map((data, index) => {
                return (
                  <Grid key={index}>
                    <Grid item xs={12} style={{ display: "flex" }}>
                      <IconButton>
                        <CalendarMonthIcon style={{ color: "#00CE3F" }} />
                      </IconButton>
                      <Typography
                        mt={1}
                        className="normal_normal_600_14_Manrope dark_grey"
                      >
                        {formatHistory(data) ||
                          `Task is due by ${moment(data?.pastDueDate).format(
                            "DD/MM/yyyy"
                          )} 
              pushed to ${moment(data?.newDueDate).format("DD/MM/yyyy")} 
              by ${data?.createdBy?.firstName || "NA"} ${
                            data?.createdBy?.lastName || "NA"
                          } 
              on ${moment(data?.createdAt).format("DD/MM/yyyy")} 
              for reason: ${data?.note || "NA"}`}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                  </Grid>
                );
              })}

            {isLoadingMore && (
              <Grid item xs={12} style={{ textAlign: "center", padding: 10 }}>
                <Typography variant="body2">Loading more...</Typography>
              </Grid>
            )}
          </Grid>
        )}

        {/* Add  Note Component */}
        {tabValue === 1 && (
          <>
            <Grid item xs={12} p={2}>
              <TextField
                type="text"
                label="Add Note"
                fullWidth
                placeholder="Enter a Note"
                multiline
                rows={5}
                value={updateDueDateData?.note}
                onChange={(e) => handleUpdateValue(e?.target?.value, "note")}
                error={isError && !updateDueDateData?.note}
                helperText={
                  isError && !updateDueDateData?.note && VALIDATION_MSG.REQUIRED
                }
                sx={{
                  "& label.Mui-focused": {
                    color: "#00ce3f",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#00ce3f",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00ce3f",
                    },
                  },
                }}
              />
            </Grid>

            {/* Date Time Picker --> Due date */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    PopperProps={{
                      anchorOrigin: { vertical: "top", horizontal: "center" },
                      transformOrigin: {
                        vertical: "bottom",
                        horizontal: "center",
                      },
                      getContentAnchorEl: null,
                    }}
                    slotProps={{
                      textField: {
                        InputLabelProps: { shrink: true },
                        readOnly: true,
                      },
                    }}
                    label="Due Date"
                    format="dd/MM/yyyy hh:mm a"
                    className="normal_normal_600_12_Manropee outlined_border"
                    value={updateDueDateData?.dueDate}
                    onChange={(e) => handleUpdateValue(e, "dueDate")}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </FormControl>
              {dateWarn && (
                <helperText style={{ color: "orange", padding: 5 }}>
                  Due date selected is before the current date and time.
                </helperText>
              )}
            </Grid>
            <Grid
              xs={12}
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "2rem 0 0 0",
                gap: "1rem",
              }}
            >
              <Button
                className="cancel_button"
                style={{ width: "130px" }}
                onClick={() => handleClose()}
              >
                Cancel
              </Button>
              <Button
                className="save_button"
                onClick={() => handleClickUpdateDueDate()}
              >
                Save
              </Button>
            </Grid>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DueDateDialog;
